import * as React from 'react';
import { Subscription } from 'rxjs/internal/Subscription';
import { fromEvent } from 'rxjs/internal/observable/fromEvent';
import { disableBodyScroll, clearAllBodyScrollLocks } from 'body-scroll-lock';
import { styles } from './styles';
import { ThemesBucket } from '../components/designBuckets/ThemeBucket';
import { LayoutBucket } from '../components/designBuckets/LayoutBucket';
import { ColorBucket } from '../components/designBuckets/ColorBucket';
import { DesignShowcaseState, DesignShowcase } from '..';
import {
  DesignShowcase_designShowcase_organization_themes_buckets,
  DesignShowcase_designShowcase_organization_colors_buckets
} from '@graphql/designShowcaseTypes';
import { Columns } from '@joykit';
import MediaQuery from 'react-responsive';
import { BaseButton } from '../../components/BaseButton';
import { InviteBucket } from '../components/designBuckets/InviteCell';
import SlidingPane from 'react-sliding-pane';
import './popup.css';
import { Icon } from '@withjoy/joykit';

interface Props {
  themeState: DesignShowcaseState['themes'];
  themeBuckets: DesignShowcase_designShowcase_organization_themes_buckets[];

  layoutState: DesignShowcaseState['layout'];
  layoutOptions: { label: string; value: string }[];

  colorState: DesignShowcaseState['colors'];
  colorBuckets: DesignShowcase_designShowcase_organization_colors_buckets[];

  invitesState: DesignShowcaseState['invites'];

  handleTriggerClick: DesignShowcase['handleTriggerClick'];
  handleOptionSelect: DesignShowcase['handleOptionSelect'];
  handleOnCloseRequest: DesignShowcase['handleOnCloseRequest'];
  handleOnResetClick: DesignShowcase['handleOnResetClick'];
  handleFilterButtonClick: DesignShowcase['handleFilterButtonClick'];
  handleDone: DesignShowcase['handleDone'];
  showMobileNav: boolean;
  forwardRef: React.RefObject<HTMLDivElement>;
}

type State = {
  fixed: boolean;
  showFilterPanel: boolean;
};

export class ShowcaseFilters extends React.Component<Props, State> {
  state = {
    fixed: false,
    showFilterPanel: false
  };
  private scrollSub: Subscription;
  private desktopFilterRef = React.createRef<HTMLDivElement>();
  targetElement: null | HTMLDivElement = null;

  componentDidMount() {
    this.observeScroll();
    this.setPlacement();
    this.targetElement = document.querySelector('#designs-mobile-nav');
  }

  componentWillUnmount() {
    if (this.scrollSub) {
      this.scrollSub.unsubscribe();
    }
    clearAllBodyScrollLocks();
  }
  observeScroll = () => {
    this.scrollSub = fromEvent(window, 'scroll').subscribe(() => {
      this.setPlacement();
    });
  };

  render() {
    const sharedBucketProps = {
      onTriggerClick: this.props.handleTriggerClick,
      onOptionSelect: this.props.handleOptionSelect,
      onCloseRequest: this.props.handleOnCloseRequest,
      onResetClick: this.props.handleOnResetClick
    };

    const { showMobileNav } = this.props;

    if (showMobileNav) {
      disableBodyScroll(this.targetElement);
    }
    if (!showMobileNav) {
      clearAllBodyScrollLocks();
    }

    return (
      <MediaQuery minWidth={678}>
        {match => {
          if (match) {
            return (
              <Columns>
                <Columns.Column>
                  <div id="showcase-filters" ref={this.desktopFilterRef} className={styles.container}>
                    <div className={styles.floater(this.state.fixed)}>
                      <ThemesBucket
                        {...this.props.themeState}
                        options={this.props.themeBuckets}
                        {...sharedBucketProps}
                      />
                      <LayoutBucket
                        {...this.props.layoutState}
                        options={this.props.layoutOptions}
                        {...sharedBucketProps}
                      />
                      <ColorBucket
                        {...this.props.colorState}
                        options={this.props.colorBuckets}
                        {...sharedBucketProps}
                      />
                      <InviteBucket {...this.props.invitesState} {...sharedBucketProps} />
                    </div>
                  </div>
                </Columns.Column>
              </Columns>
            );
          }

          return (
            <>
              <div id="showcase-filters" className={styles.floatingButtonContainer} ref={this.desktopFilterRef}>
                <BaseButton
                  className={styles.floatingButton(this.state.fixed)}
                  onClick={this.props.handleFilterButtonClick}
                >
                  Filter Designs
                </BaseButton>
              </div>
              <SlidingPane
                closeIcon={<Icon source="close" />}
                width="100vw"
                from="bottom"
                forwardRef={this.props.forwardRef}
                id="designs-mobile-nav"
                isOpen={showMobileNav}
                overlayClassName={styles.popupPosition}
                onRequestClose={this.props.handleDone}
              >
                <div className={styles.designPanelInner}>
                  <h1 className={styles.header}>Filter designs</h1>
                  <ThemesBucket
                    {...this.props.themeState}
                    options={this.props.themeBuckets}
                    {...sharedBucketProps}
                    isMobile={true}
                  />
                  <LayoutBucket
                    {...this.props.layoutState}
                    options={this.props.layoutOptions}
                    {...sharedBucketProps}
                    isMobile={true}
                  />
                  <ColorBucket
                    {...this.props.colorState}
                    options={this.props.colorBuckets}
                    {...sharedBucketProps}
                    isMobile={true}
                  />
                  <InviteBucket {...this.props.invitesState} {...sharedBucketProps} isMobile={true} />
                  <div className={styles.doneButtonWrapper}>
                    <BaseButton className={styles.doneButton} onClick={this.props.handleDone}>
                      View Results
                    </BaseButton>
                  </div>
                </div>
              </SlidingPane>
            </>
          );
        }}
      </MediaQuery>
    );
  }
  private setPlacement = () => {
    const node = this.desktopFilterRef.current;
    if (node) {
      const { top } = node.getBoundingClientRect();
      const targetTop = window.outerWidth < 679 ? 20 : 0;

      if (top <= targetTop && !this.state.fixed) {
        this.setState({ fixed: true });
      } else if (top > targetTop && this.state.fixed) {
        this.setState({ fixed: false });
      }
    }
  };
}
